<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div class="calendar">
    <!-- Start top Bar -->
    <div class="top-bar">
      <div class="calendar-top px-10">
        <router-link
          :to="{
            name: 'Dashboard',
            params: { locale: $i18n.locale },
          }"
        >
          <div class="back-icon">
            <back-arrow class="m-auto right-back-arrow" />
          </div>
        </router-link>
        <p class="calendar-title">{{ $t('my_calendar') }}</p>
      </div>
    </div>
    <!-- end top Bar -->
    <div class="calendar-main-section user-calendar">
      <div class="full-width w-600">
        <div class="search-container">
          <base-search-select
            :options="['meeting_name', 'description']"
            @searchQuery="getSearchQuery"
            @selectedOptions="getSelectedOptions"
            :defaultOption="'meeting_name'"
            :height="'50'"
          />
        </div>
        <div class="filter-container" v-outside="closeFilterTabs">
          <div class="flex items-center justify-between">
            <span class="filter-title">{{ $t('filters') }}</span>
            <span
              class="font-semibold text-primary text-xs cursor-pointer"
              @click="clearFiltersHandler"
              v-if="isDisplayClearFilters"
            >
              {{ $t('clear_filters') }}
            </span>
          </div>
          <div class="filter-option-container" v-for="category in filterCategories" :key="category">
            <div @click="popoverToggle(category)" class="py-15">
              <div class="filter-option-title">
                <div class="flex items-center gap-x-5">
                  <span class="font-semibold text-14 test-title">
                    {{ category === 'organizers' ? $t('organizer') : $t(category) }}
                  </span>
                  <div
                    v-if="selectedFilters[category].length > 0"
                    class="w-5 h-5 bg-primaryColor rounded-full"
                  ></div>
                </div>
                <down-arrow :class="{ 'transform rotate-180': activeFilterTab === category }" />
              </div>
              <div
                v-if="selectedFilters[category].length > 0 && activeFilterTab !== category"
                class="flex items-center flex-wrap"
              >
                <div v-for="(selectedFilterItem, index) in selectedFilters[category]"
                 :key="selectedFilterItem.id">
                  <span class="text-subtitle">
                    {{ selectedFilterItem.name }}
                    {{ index < selectedFilters[category].length - 1 ? ', ' : '' }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="activeFilterTab === category" class="filter-options">
              <div v-if="category === 'organizers' || category === 'attendees'"
              class="pt-10 pb-20">
                <base-simple-search-box
                  :id="`${category}_filter_calendar`"
                  width="full"
                  height="40"
                  type="attendee"
                  @searchedText="getSearchedText(category, $event)"
                />
              </div>
              <div v-for="option in serverOptions[category]" :key="option.id" class="pb-20">
                <label :for="`filter-company-${option.name}`" style="display: flex">
                  <input
                    type="checkbox"
                    :id="`filter-company-${option.id}`"
                    class="check-box-classes"
                    v-model="selectedFilters[category]"
                    :value="option"
                  />
                  <div class="px-10" v-if="category === 'organizers' || category === 'attendees'">
                    <img
                    v-if="option.avatar != null"
                    :src="resizer(option.avatar)"
                    class="w-20 h-20 rounded-full"
                    alt="avatar"
                    :class="[imgError ? 'object-scale-down' : 'object-cover']"
                    @error="imageError($event)"
                  />
                  <name-avatar v-else rounded="full" size="20" :text="option.name" />
                  </div>
                  <span class="font-semibold truncate w-200 block text-14"
                  :class="{'px-10' : category !== 'organizers' && category !== 'attendees'}">
                    {{ option.name }}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <div class="filter-option-container">
            <div @click="openDurationSection()" class="py-15">
              <div class="filter-option-title">
                <div class="flex items-center gap-x-5">
                  <span class="font-semibold text-14 test-title">{{ $t('time_period') }}</span>
                  <div v-if="isTimeChanged" class="w-5 h-5 bg-primaryColor rounded-full"></div>
                </div>
                <down-arrow :class="{ 'transform rotate-180': activeFilterTab === 'duration' }" />
              </div>
            </div>
            <div v-if="activeFilterTab === 'duration'" class="py-20">
              <div class="border border-default rounded" v-outside="closeDurationTab">
                <div>
                  <div
                    class="flex items-center justify-between px-15 py-5 h-50"
                    @click="popoverToggleDuration('start')"
                  >
                    <span class="font-normal text-14">{{ $t('start') }}</span>
                    <div class="flex items-center gap-x-5">
                      <span
                        class="text-subtitle font-normal text-14"
                        v-if="!startHours && !startMinutes"
                      >
                        {{ $t('select') }}
                      </span>
                      <span class="text-subtitle font-normal text-14" dir="ltr" v-else>
                        {{ newFormatDateStart }} {{ startHours }}:{{ startMinutes }}
                      </span>
                      <down-arrow
                        :color="'#818181'"
                        :class="{ 'transform rotate-180': activeDurationTab === 'start' }"
                      />
                    </div>
                  </div>
                  <div v-if="activeDurationTab === 'start'" class="py-10">
                    <div class="overflow-auto flex justify-center">
                      <date-picker
                        format="YYYY-MM-DD"
                        :locale-config="{
                          fa: {
                            displayFormat: 'jDD jMMMM jYYYY',
                            lang: { label: 'شمسی' },
                          },
                          en: {
                            displayFormat: 'DD MMMM YYYY',
                            lang: { label: 'Gregorian' },
                          },
                        }"
                        :locale="calendar === 'Solar' ? 'fa' : 'en'"
                        v-model="startDate"
                        inline
                        @change="startCalendarChangeDateHandler"
                        custom-input=".calendar-hidden-input"
                        :highlight="highlight"
                      />
                    </div>
                    <div class="time-picker-group">
                      <scroll-picker
                        :options="startHourOptions"
                        :class="{ 'last-days': isDaysBefore }"
                        v-model="startHours"
                      />
                      <span class="clock-separator" :class="{ 'last-days': isDaysBefore }">:</span>
                      <scroll-picker
                        :options="startMinuteOptions"
                        :class="{ 'last-days': isDaysBefore }"
                        v-model="startMinutes"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div
                    class="flex items-center justify-between px-15 py-5 h-50
                     border-t border-default"
                    @click="popoverToggleDuration('end')"
                  >
                    <span class="font-normal text-14">{{ $t('end') }}</span>
                    <div class="flex items-center gap-x-5">
                      <span
                        class="text-subtitle font-normal text-14"
                        v-if="!endHours && !endMinutes"
                      >
                        {{ $t('select') }}
                      </span>
                      <span class="text-subtitle font-normal text-14" dir="ltr" v-else>
                        {{ newFormatDateEnd }} {{ endHours }}:{{ endMinutes }}
                      </span>
                      <down-arrow
                        :color="'#818181'"
                        :class="{ 'transform rotate-180': activeDurationTab === 'end' }"
                      />
                    </div>
                  </div>
                  <div v-if="activeDurationTab === 'end'" class="py-10">
                    <div class="overflow-auto flex justify-center">
                      <date-picker
                        format="YYYY-MM-DD"
                        :locale-config="{
                          fa: {
                            displayFormat: 'jDD jMMMM jYYYY',
                            lang: { label: 'شمسی' },
                          },
                          en: {
                            displayFormat: 'DD MMMM YYYY',
                            lang: { label: 'Gregorian' },
                          },
                        }"
                        :locale="calendar === 'Solar' ? 'fa' : 'en'"
                        v-model="endDate"
                        inline
                        @change="endCalendarChangeDateHandler"
                        custom-input=".calendar-hidden-input"
                        :highlight="endHighlight"
                      />
                    </div>
                    <div class="time-picker-group">
                      <scroll-picker
                        :options="endHourOptions"
                        :class="{ 'last-days': isDaysBefore }"
                        v-model="endHours"
                      />
                      <span class="clock-separator" :class="{ 'last-days': isDaysBefore }">:</span>
                      <scroll-picker
                        :options="endMinuteOptions"
                        :class="{ 'last-days': isDaysBefore }"
                        v-model="endMinutes"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="filter-option-container">
            <div @click="popoverToggle('presence')" class="py-15">
              <div class="filter-option-title">
                <div class="flex items-center gap-x-5">
                  <span class="font-semibold text-14 test-title">{{ $t('presence_status') }}</span>
                  <div
                    v-if="selectedFilters.presence.length > 0"
                    class="w-5 h-5 bg-primaryColor rounded-full"
                  ></div>
                </div>
                <down-arrow :class="{ 'transform rotate-180': activeFilterTab === 'presence' }" />
              </div>
              <div
                v-if="selectedFilters.presence.length > 0 && activeFilterTab !== 'presence'"
                class="flex items-center flex-wrap"
              >
                <div>
                  <span class="text-subtitle">{{ $t(`${selectedFilters.presence}`) }}</span>
                </div>
              </div>
            </div>
            <div v-if="activeFilterTab === 'presence'" class="filter-options">
              <div
                v-for="(option, index) in serverOptions.presence"
                :key="index"
                class="pb-20 flex items-center px-3 pt-3"
              >
                <input
                  type="radio"
                  :id="`type-${option}`"
                  class="hidden"
                  :value="option"
                  v-model="selectedFilters.presence"
                />
                <label class="items-center" :for="`type-${option}`">
                  <span class="radio-inner" />
                </label>
                <span class="font-semibold px-10 truncate w-300 block text-14">
                  {{ $t(`${option}`) }}
                </span>
              </div>
            </div>
          </div>
          <div class="filter-description">
            <span class="font-semibold text-14">{{ $t('description') }}</span>
            <base-state-checkbox v-model="selectedFilters.description" />
          </div>
        </div>
        <div class="overflow-auto flex justify-center">
          <date-picker
            format="YYYY-MM-DD"
            :locale-config="{
              fa: {
                displayFormat: 'jDD jMMMM jYYYY',
                lang: { label: 'شمسی' },
              },
              en: {
                displayFormat: 'DD MMMM YYYY',
                lang: { label: 'Gregorian' },
              },
            }"
            :locale="calendar === 'Solar' ? 'fa' : 'en'"
            v-model="date"
            inline
            @change="formatDateHandler"
            custom-input=".calendar-hidden-input"
            :highlight="highlight"
          />
        </div>
      </div>
      <div class="full-width w-full">
        <div v-if="isDisplayFilterResults && filterResults.length === 0"
        class="flex justify-center items-center w-full h-full">
          <span class="text-disabled font-normal text-18">{{ $t('no_item_found') }}</span>
        </div>
        <full-event-calender
          v-else
          markOverLappingBorders
          :isPinchActive="false"
          :oneHourInPixel="80"
          :dateToStart="new Date(date)"
          type="day"
          :locale="$i18n.locale === 'fa' ? 'fa-IR' : 'en-US'"
          :rtl="$i18n.locale === 'fa' || $i18n.locale === 'ar'"
          :eventList="extractMeeting"
          :calendar="calendar === 'Solar' ? 'persian' : 'gregory'"
          :theme="theme"
          hideHeader
          @eventClicked="eventBoxClick"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ScrollPicker } from 'vue-scroll-picker';
import VuePersianDatetimePicker from 'vue-persian-datetime-picker';
import axiosConfig from '@/services/axios.config';
import BackArrow from '@/assets/icons/vue/backArrow.vue';
import imageResizer from '@/utils/image-resizer';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import getUser from '@/services/profile/user.service';
import i18n from '@/utils/i18n/i18n';
import convertToBinary from '@/utils/boolean-converter';
import DownArrow from '@/assets/icons/vue/downArrow.vue';
import BaseSearchSelect from '@/components/base/BaseSearchSelect.vue';
import NameAvatar from '@/components/base/BaseNameAvatar.vue';
import BaseStateCheckbox from '../components/base/BaseStateCheckbox.vue';
import BaseSimpleSearchBox from '../components/base/BaseSimpleSearchBox.vue';

const moment = require('moment-jalaali');

export default {
  name: 'UserCalendar',
  components: {
    datePicker: VuePersianDatetimePicker,
    BackArrow,
    DownArrow,
    ScrollPicker,
    BaseSearchSelect,
    BaseStateCheckbox,
    NameAvatar,
    BaseSimpleSearchBox,
  },
  mounted() {
    const user = JSON.parse(localStorage.getItem('user'));
    this.calendar = user.calendar_type;
    this.getUserReservationsHandler();
    this.getUserFilterOptions();
  },
  data() {
    return {
      date: this.$route.query.date || new Date().toISOString().split('T')[0],
      calendar: '',
      newFormatDateStart: '',
      newFormatDateEnd: '',
      imgError: false,
      locale: localStorage.locale,
      startDate: null,
      endDate: null,
      startHours: null,
      startMinutes: null,
      endHours: null,
      endMinutes: null,
      activeFilterTab: '',
      activeDurationTab: '',
      searchedText: '',
      selectedFilters: {
        companies: [],
        zones: [],
        devices: [],
        organizers: [],
        attendees: [],
        presence: '',
        description: false,
      },
      serverOptions: {
        companies: [],
        zones: [],
        devices: [],
        organizers: [],
        attendees: [],
        presence: ['yes', 'no', 'maybe'],
      },
      selectedOPtions: [],
      filterResults: [],
      isDisplayClearFilters: false,
      isTimeChanged: false,
      filterCategories: ['companies', 'zones', 'devices', 'organizers', 'attendees'],
      isDisplayFilterResults: false,
    };
  },
   watch: {
    selectedFilters: {
      handler(val) {
        const isEmpty = Object.keys(val).every((key) => {
          const value = val[key];
          return Array.isArray(value) ? value.length === 0 : !value;
        });
        if (!isEmpty) {
          this.isDisplayClearFilters = true;
          this.filterMyCalendar();
        } else if (isEmpty
         && this.startAndEndTime === 'null-null-null-null-null-null' && this.searchedText === '') {
          this.isDisplayClearFilters = false;
          this.isDisplayFilterResults = false;
        } else if (isEmpty
        && this.startAndEndTime === 'null-null-null-null-null-null' && this.searchedText) {
          this.filterMyCalendar();
          this.isDisplayClearFilters = false;
        }
      },
      deep: true,
    },
    selectedOPtions() {
      if (this.searchedText !== '') {
        this.filterMyCalendar();
      }
    },
    searchedText() {
      this.filterMyCalendar();
    },
    startAndEndTime(val) {
      this.initiateDate();
      if (val !== 'null-null-null-null-null-null') {
        this.isTimeChanged = true;
        this.isDisplayClearFilters = true;
        this.filterMyCalendar();
      }
    },
    activeFilterTab(val, oldVal) {
      if (oldVal === 'attendees' || oldVal === 'organizers') {
        this.getUserFilterOptions();
      }
    },
  },
  computed: {
    ...mapGetters({
      conflictInfo: 'reserve/getConflict',
      theme: 'getTheme',
      defaultCompany: 'getDefaultCompany',
      userInformation: 'getUserInformation',
    }),
    startAndEndTime() {
      return `${this.startDate}-${this.startHours}-${this.startMinutes}-${this.endDate}-${this.endHours}-${this.endMinutes}`;
    },
    startHourOptions() {
      return Array.from({ length: 24 }, (_, i) => this.zeroPadded(i));
    },
    startMinuteOptions() {
      // eslint-disable-next-line max-len
      const range = (start, stop, step) => Array.from({ length: (stop - start) / step + 1 }, (_, i) => this.zeroPadded(start + i * step));
      return range(0, 55, 5);
    },
    endHourOptions() {
      return Array.from({ length: 24 }, (_, i) => this.zeroPadded(i));
    },
    endMinuteOptions() {
      // eslint-disable-next-line max-len
      const range = (start, stop, step) => Array.from({ length: (stop - start) / step + 1 }, (_, i) => this.zeroPadded(start + i * step));
      return range(0, 55, 5);
    },
    extractMeeting() {
      let arr = [];
      if (this.isDisplayFilterResults) {
        this.filterResults.forEach((meeting) => {
          arr.push(meeting);
        });
      } else {
        this.conflictInfo.forEach((meeting) => {
          arr = arr.concat(meeting.meetings);
        });
      }
      const events = [...arr];
      events.forEach((event) => {
        if (event.status !== 'approve' && event.status) {
          // eslint-disable-next-line no-param-reassign
          event.notInjectBackGroundColors = true;
          // eslint-disable-next-line no-param-reassign
          event.hideIsGoing = true;
          // eslint-disable-next-line no-param-reassign
          event.classList = [' event-border'];
          if (event.status === 'pending') {
            // eslint-disable-next-line no-param-reassign
            event.badgeClassList = [' warning-badge'];
            // eslint-disable-next-line no-param-reassign
            event.badge = i18n.t('awaiting_confirmation');
          } else {
            // eslint-disable-next-line no-param-reassign
            event.badgeClassList = [' danger-badge'];
            // eslint-disable-next-line no-param-reassign
            event.badge = i18n.t('rejected');
          }
        }
      });
      return events;
    },
    isDaysBefore() {
      const today = moment().endOf('day').format('YYYY-MM-DD');
      return moment(this.date).isBefore(moment(today));
    },
  },
  methods: {
    closeDurationTab() {
      this.activeDurationTab = '';
    },
    initiateDate() {
      if (this.calendar === 'Solar') {
        this.newFormatDateEnd = moment(this.endDate).format('jYYYY-jMM-jDD');
        this.newFormatDateStart = moment(this.startDate).format('jYYYY-jMM-jDD');
      } else {
        this.newFormatDateEnd = moment(this.endDate).format('YYYY-MM-DD');
        this.newFormatDateStart = moment(this.startDate).format('YYYY-MM-DD');
      }
    },
    imageError($event) {
      this.imgError = true;
      this.OnImageError($event);
    },
    getSearchedText(option, searchText) {
      if (option === 'attendees') {
        this.getUserFilterOptions('attendee', searchText);
      } else if (option === 'organizers') {
        this.getUserFilterOptions('organizer', searchText);
      }
    },
    resizer(image) {
      return imageResizer(image, this.imageSize);
    },
    closeFilterTabs() {
      this.activeFilterTab = '';
      this.activeDurationTab = '';
    },
    openDurationSection() {
      this.popoverToggle('duration');
      if (!this.isTimeChanged) {
        this.getNextAvailableTime();
      }
    },
    clearFiltersHandler() {
      this.selectedFilters = {
        companies: [],
        zones: [],
        devices: [],
        organizers: [],
        attendees: [],
        presence: '',
        description: false,
      };
      this.isDisplayClearFilters = false;
      this.isDisplayFilterResults = false;
      this.isTimeChanged = false;
      this.startDate = null;
      this.endDate = null;
      this.startHours = null;
      this.startMinutes = null;
      this.endHours = null;
      this.endMinutes = null;
    },
    getNextAvailableTime() {
      this.startDate = new Date().toISOString().split('T')[0];
      this.endDate = new Date().toISOString().split('T')[0];
      const hours = Array.from(Array(24).keys());
      const mins = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
      const today = new Date();
      const userHour = today.getHours();
      const userMin = today.getMinutes();
      const availableHours = hours.filter((h) => (userMin > 55 ? h > userHour : h >= userHour));
      const availableMins = mins.filter((el) => el >= userMin);
      if (!availableMins.length) {
        availableMins.push(0);
      }
      this.startHours = this.zeroPadded(availableHours[0]);
      this.startMinutes = this.zeroPadded(availableMins[0]);
      const availableEndTime = moment(this.startDate)
        .set({
          hour: availableHours[0],
          minute: availableMins[0],
        })
        .add(30, 'minutes');
      this.endHours = this.zeroPadded(availableEndTime.hours());
      this.endMinutes = this.zeroPadded(availableEndTime.minutes());
      this.endDate = `${availableEndTime.format('YYYY-MM-DD')}`;
    },
    filterMyCalendar() {
      const formData = new FormData();
      formData.append(
        'is_description',
        this.selectedFilters.description ? convertToBinary(this.selectedFilters.description) : '',
      );
      // SEND DATE IF BETWEEN START AND END,
      //  SEND START DATE IF OUTSIDE OF RANGE,
      //  SEND DATE IF START AND END AND DATE EQUAL
      if (this.startAndEndTime !== 'null-null-null-null-null-null') {
        const checkDateSame = moment(this.date).isSame(this.startDate, 'day') && moment(this.date).isSame(this.endDate, 'day');
        const isDateWithinStartAndBeforeEnd = moment(this.date).isSameOrAfter(this.startDate)
          && moment(this.date).isBefore(this.endDate);
        const isDateAfterStartAndWithinEnd = moment(this.date).isAfter(this.startDate)
          && moment(this.date).isSameOrBefore(this.endDate);
        if (checkDateSame) {
          formData.append(
            'started_at',
            `${this.startDate} ${this.startHours}:${this.startMinutes}:00`,
          );
          formData.append('ended_at', `${this.endDate} ${this.endHours}:${this.endMinutes}:00`);
        } else if (isDateWithinStartAndBeforeEnd || isDateAfterStartAndWithinEnd) {
          formData.append(
          'started_at',
          `${this.date.slice(0, 10)} 00:00:00`,
        );
        formData.append('ended_at', `${this.date.slice(0, 10)} 23:59:59`);
        } else {
          formData.append(
          'started_at',
          `${this.startDate} 00:00:00`,
        );
        formData.append('ended_at', `${this.startDate} 23:59:59`);
        }
      }
      formData.append('user_confirm', this.selectedFilters.presence);
      formData.append('search_field', this.searchedText);
      formData.append(
        'is_description_search',
        this.selectedOPtions.includes('description') ? 1 : '',
      );
      formData.append('is_name_search', this.selectedOPtions.includes('meeting_name') ? 1 : '');
      const companyIds = this.selectedFilters.companies.map((company) => company.id);
      if (companyIds.length > 0) {
        formData.append('company_ids[]', companyIds);
      }
      const zoneIds = this.selectedFilters.zones.map((zone) => zone.id);
      if (zoneIds.length > 0) {
        formData.append('zone_ids[]', zoneIds);
      }
      const organizerIds = this.selectedFilters.organizers.map((organizer) => organizer.id);
      if (organizerIds.length > 0) {
        formData.append('organizer_ids[]', organizerIds);
      }
      const deviceIds = this.selectedFilters.devices.map((device) => device.id);
      if (deviceIds.length > 0) {
        formData.append('device_ids[]', deviceIds);
      }
      const attendeeIds = this.selectedFilters.attendees.map((attendee) => attendee.id);
      if (attendeeIds.length > 0) {
        formData.append('attendee_ids[]', attendeeIds);
      }
      axiosConfig
        .post('user/my-calendar/search', formData)
        .then((response) => {
          this.filterResults = response.data.data;
          this.isDisplayFilterResults = true;
        })
        .catch((error) => {
          serverErrorsHandler(error);
          throw error;
        });
    },
    getUserFilterOptions(option, searchText) {
      axiosConfig
        .get(`/user/my-calendar/filters?search=${searchText || ''}&type=${option || ''}`)
        .then((response) => {
          this.serverOptions.companies = response.data.data.companies.map((company) => ({
            id: company.id,
            name: company.name,
          }));
          this.serverOptions.zones = response.data.data.zones.map((zone) => ({
            id: zone.id,
            name: zone.name,
          }));
          this.serverOptions.devices = response.data.data.devices.map((device) => ({
            id: device.id,
            name: device.name,
          }));
          this.serverOptions.attendees = response.data.data.attendees.map((attendee) => ({
            id: attendee.id,
            name: attendee.name,
            avatar: attendee.avatar,
          }));
          this.serverOptions.organizers = response.data.data.organizer.map((organizer) => ({
            id: organizer.id,
            name: organizer.name,
            avatar: organizer.avatar,
          }));
        })
        .catch((error) => {
          serverErrorsHandler(error);
          throw error;
        });
    },
    getSearchQuery(searchedText) {
      this.searchedText = searchedText;
    },
    getSelectedOptions(selectedOPtions) {
      this.selectedOPtions = selectedOPtions;
    },
    endHighlight(formatted) {
      const attributes = { title: `Today is ${this.date}` };
      if (moment(formatted).isBefore(moment(this.date))) {
        attributes.class = 'highlighted-disabled';
      }
      return attributes;
    },
    startCalendarChangeDateHandler(event) {
      const newStartDate = this.calendar === 'Solar' ? event.format('jYYYY-jMM-jDD') : event.format('YYYY-MM-DD');
      this.newFormatDateStart = newStartDate;
      this.endDate = this.startDate;
      this.newFormatDateEnd = this.newFormatDateStart;
    },
    endCalendarChangeDateHandler(event) {
      const newEndDate = this.calendar === 'Solar' ? event.format('jYYYY-jMM-jDD') : event.format('YYYY-MM-DD');
      this.newFormatDateEnd = newEndDate;
    },
    zeroPadded(num) {
      return num < 10 ? `0${num}` : num;
    },
    popoverToggleDuration(data) {
      if (this.activeDurationTab === data) {
        this.activeDurationTab = '';
      } else {
        this.activeDurationTab = data;
      }
    },
    popoverToggle(data) {
      if (this.activeFilterTab === data) {
        this.activeFilterTab = '';
      } else {
        this.activeFilterTab = data;
      }
    },
    eventBoxClick(event) {
      const companyModel = this.userInformation.companies.find(
        (company) => company.id === event.company_id,
      );
      if (event.company_id !== this.defaultCompany.id && !event.is_guest) {
        this.$store.dispatch('defaultCompany', companyModel);
        axiosConfig
          .post('/user/change-default-company', { company_id: event.company_id })
          .then(async () => {
            localStorage.removeItem('user');
            await getUser();
          });
        this.$router.push({
          name: 'MeetingDetail',
          params: {
            reserveId: event.id,
            deviceId: event.device_id,
            activePage: 'meeting_detail',
            companyId: event.company_id,
          },
        });
      } else {
        this.$router.push({
          name: 'MeetingDetail',
          params: {
            reserveId: event.id,
            deviceId: event.device_id,
            activePage: 'meeting_detail',
            companyId: event.company_id,
          },
        });
      }
    },
    highlight(formatted) {
      const today = moment().endOf('day').format('YYYY-MM-DD');
      const attributes = { title: `Today is ${today}` };
      if (moment(formatted).isBefore(moment(today))) {
        attributes.class = 'highlighted-disabled';
      }
      return attributes;
    },
    formatDateHandler() {
      const isEmpty = Object.keys(this.selectedFilters).every((key) => {
          const value = this.selectedFilters[key];
          return Array.isArray(value) ? value.length === 0 : !value;
        });
      if (!isEmpty || this.searchedText !== '' || this.startAndEndTime !== 'null-null-null-null-null-null') {
        this.filterMyCalendar();
      }
      this.getUserReservationsHandler();
    },
    getUserReservationsHandler() {
      axiosConfig
        .get(`/user/my-calendar?current_date=${this.date}`)
        .then((response) => {
          this.$store.commit('reserve/SET_CONFLICT', response.data.data);
        })
        .catch((error) => {
          serverErrorsHandler(error);
          throw error;
        });
    },
  },
};
</script>

<style lang="css" scoped>
@media (max-width: 768px) {
  .user-calendar {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .full-width {
    width: 100%;
  }
}
.search-container {
  @apply mt-20;
}
.filter-container {
  @apply border border-default rounded w-full p-15 mt-20;
}
.filter-title {
  @apply text-title font-bold text-16;
}
.filter-option-container {
  @apply border border-default border-r-0 border-l-0 border-t-0;
}
.filter-option-title {
  @apply cursor-pointer flex items-center justify-between;
}
.filter-options {
  @apply flex flex-col;
  max-height: 200px;
  overflow: auto;
}
.filter-description {
  @apply flex items-center justify-between;
  padding-top: 15px;
}
.calendar {
  @apply bg-secondary p-32 rounded;
}
.calendar-top {
  @apply flex items-center justify-between w-max;
}
.calendar-title {
  @apply w-full text-left mx-20 text-18 font-bold;
}
.calendar-main-section {
  @apply flex gap-x-10 py-10 px-0;
}
.back-icon {
  @apply w-50 h-50 rounded flex border-2 border-default left-50 cursor-pointer;
}
.top-bar {
  @apply flex flex-row items-center justify-between;
}
</style>
<style>
.highlighted-disabled > span {
  @apply text-title;
}
.vue-scroll-picker-layer .top,
.vue-scroll-picker-layer .bottom {
  @apply rounded-sm border-none text-title opacity-80 bg-theme-secondary bg-none;
}
.vue-scroll-picker-item {
  @apply text-36 my-20;
}
.last-days .vue-scroll-picker-layer .middle {
  @apply opacity-80 bg-theme-secondary bg-none;
}
.last-days {
  @apply opacity-80 bg-theme-secondary bg-none;
}
.time-picker-group {
  @apply flex border rounded-sm border-default mx-15 mt-10;
  direction: ltr;
}
.clock-separator {
  @apply text-36 self-center;
}
.vpd-is-inline {
  @apply rounded-sm;
}
.vpd-header {
  @apply rounded-t-sm bg-secondary !important;
}
.vpd-header {
  @apply bg-secondary text-title;
}
.vpd-selected .vpd-day-text {
  @apply text-secondary;
}
.vpd-day-text {
  @apply text-blueText;
}
.vpd-content,
.vpd-container {
  @apply bg-secondary rounded-sm;
}
.vpd-container {
  @apply border-none;
}
.vpd-prev,
.vpd-next {
  @apply flex justify-center items-center;
}
.vpd-header {
  @apply pt-20;
}
.vpd-month-label {
  @apply w-110 !important;
}
.vpd-day-effect {
  @apply bg-blue !important;
}
</style>
<style lang="scss">
.highlighted-disabled {
  > span {
    @apply text-subtitle;
  }
}
</style>
